import { useState } from "react";
import { verdict, framing, hasGaps } from "../verdict.js";
import { shareText, shareOrCopy } from "../share.js";
import Icon from "../ui/Icon.jsx";
import Save from "./Save.jsx";
import PeerCompare from "../ui/PeerCompare.jsx";
import AdSlot from "../ui/AdSlot.jsx";

export default function Result({ session, onNew, showSave = true, onBack = null }) {
  const { debate, outcome } = session;
  const [tab, setTab] = useState("cards");
  const [saving, setSaving] = useState(false);
  const [shared, setShared] = useState(null);

  const cards = outcome.outcome?.consequences ?? [];
  const report = outcome.outcome?.weakness_report ?? {};
  const start = debate.convictionStart;
  const end = outcome.convictionEnd;
  const evasions = outcome.evasions ?? 0;
  const { heading, sub } = framing(debate.topicSnapshot?.frame);
  const gaps = hasGaps(report);

  return (
    <main className="screen" style={{ minHeight: "100vh" }}>
      <header className="bar">
        {onBack
          ? <button type="button" className="icon-btn" aria-label="뒤로" onClick={onBack}><Icon name="back" /></button>
          : <a href="/" className="icon-btn" aria-label="처음으로"><Icon name="close" /></a>}
        <span className="grow" />
        <nav aria-label="결과" style={{ display: "flex", padding: 3, border: "1px solid var(--line)", borderRadius: 999, background: "var(--white)" }}>
          {[["cards", "귀결 카드"], ["report", "아직 답이 없는 곳"]].map(([k, text]) => (
            <button key={k} type="button" onClick={() => setTab(k)} aria-current={tab === k ? "page" : undefined}
              style={{
                minHeight: 36, padding: "0 14px", border: 0, borderRadius: 999, cursor: "pointer",
                fontFamily: "inherit", fontSize: 13, fontWeight: 800,
                background: tab === k ? "var(--ink)" : "transparent",
                color: tab === k ? "var(--paper)" : "var(--ink)",
              }}>
              {text}
            </button>
          ))}
        </nav>
      </header>

      <span className="pill" style={{ alignSelf: "flex-start", fontSize: 12 }}>
        {debate.topicSnapshot?.title} · {debate.stanceLabel}
      </span>

      <section style={{ padding: "16px 18px", borderRadius: 4, background: "var(--ink)", color: "var(--paper)", display: "flex", flexDirection: "column", gap: 6 }}>
        <span className="num" style={{ fontSize: 12, fontWeight: 800, color: "var(--red)" }}>
          확신도 {start} → {end} · 회피 {evasions}
        </span>
        <span className="d" style={{ fontSize: 25, lineHeight: 1.25 }}>
          {verdict({ convictionStart: start, convictionEnd: end, evasions })}
        </span>
      </section>

      {tab === "cards" ? (
        <>
          <h1 className="title">{heading}</h1>
          <p className="note">{sub}</p>
          <ul className="list grid">
            {cards.map((c, i) => (
              <li key={i} className="card cream raised" style={{ borderRadius: 4 }}>
                <span className="num note">{String(i + 1).padStart(2, "0")} / {String(cards.length).padStart(2, "0")}</span>
                <h2 className="d" style={{ fontSize: 30, lineHeight: 1.16 }}>{c.title}</h2>
                <p style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.65, textWrap: "pretty" }}>{c.consequence}</p>
                {/* 주사색 바탕에는 흰 글자만 올린다 — 먹색은 3.75:1, .label 의 faint 는 1.42:1 로
                    결과 화면에서 제일 중요한 칸이 제일 안 읽혔다. 흰색이면 4.86:1. */}
                <div style={{ padding: "12px 14px", borderRadius: 4, background: "var(--red)", color: "var(--white)", display: "flex", flexDirection: "column", gap: 4 }}>
                  <span className="label" style={{ color: "var(--white)", opacity: 0.85 }}>깨지는 전제</span>
                  <span style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.5 }}>{c.broken_premise}</span>
                </div>
                {/* falsifier 는 접거나 숨기지 않는다. 이게 이 서비스가 확증편향 기계가 아니라는 유일한 장치다. */}
                <div style={{ padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 4, background: "var(--paper)", display: "flex", flexDirection: "column", gap: 4 }}>
                  <span className="label">틀릴 수도 있는 이유</span>
                  <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.5 }}>{c.falsifier}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <h1 className="title">{gaps ? "아직 답이 없는 곳" : "이번엔 뚫린 곳이 없었어요"}</h1>
          {!gaps && (
            <section className="card ai raised">
              <span aria-hidden="true" style={{
                alignSelf: "flex-start", width: 46, height: 46, border: "1px solid var(--line)", borderRadius: 999,
                background: "var(--paper)", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon name="check" size={24} width={3} />
              </span>
              <p style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.6 }}>
                회피하거나 근거가 얇았던 지점이 없어요. 방어를 잘 하셨어요.
              </p>
            </section>
          )}

          {report.strongest_point && (
            <article className="card mark">
              <h2 className="label">잘 버틴 곳</h2>
              <p style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.6, textWrap: "pretty" }}>{report.strongest_point}</p>
            </article>
          )}

          <Gap title="답하지 않은 것" items={report.unanswered} />
          <Gap title="근거가 조금 더 필요한 곳" items={report.thin_evidence} />

          {report.position_shift_summary && (
            <section className="card ai">
              <h2 className="label">입장은 어떻게 움직였나</h2>
              <p style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.55 }}>{report.position_shift_summary}</p>
            </section>
          )}
        </>
      )}

      {/* 귀결 카드를 다 본 뒤에 놓는다. 위에 두면 결과보다 동의 요청이 먼저 보인다. */}
      <PeerCompare debate={debate} />

      <span className="grow" />

      <p className="note" style={{ textAlign: "center" }}>
        AI가 생성한 사고 실험이에요. 예측이나 전망이 아니에요.
      </p>

      {shared && (
        <p role="status" className="note" style={{ textAlign: "center" }}>
          {shared === "copied" ? "링크를 복사했어요" : shared === "failed" ? "복사가 안 됐어요. 주소창을 그대로 쓰셔도 돼요." : null}
        </p>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
        <button type="button" className="btn primary" onClick={async () => {
          setShared(await shareOrCopy({
            text: shareText({
              topic: debate.topicSnapshot?.title,
              stanceLabel: debate.stanceLabel,
              share: outcome.outcome?.share,
            }),
            // ★ 공개된 것만 결과 주소를 준다. 비공개 결과 주소를 뿌리면
            // 받은 사람은 「없어요」 페이지만 보고, 공개 여부도 흘리게 된다.
            url: debate.isPublic
              ? `${window.location.origin}/a/${debate.id}`
              : window.location.origin,
          }));
        }}>
          공유하기
        </button>
        <button type="button" className="btn" onClick={onNew}>다른 주제</button>
      </div>

      {!debate.isPublic && (
        <p className="note" style={{ textAlign: "center" }}>
          아카이브에 올리면 이 결과 주소로 공유할 수 있어요. 지금은 링크만 가요.
        </p>
      )}

      {showSave && (
        // 로그인해야 토론할 수 있으므로 이미 저장돼 있다. 「저장해 두기」는 거짓말이다.
        <button type="button" className="btn go" onClick={() => setSaving(true)}>
          {debate.isPublic ? "아카이브 공개 설정" : "아카이브에 올리기"}
        </button>
      )}

      {/* 광고는 맨 끝. 귀결 카드와 버튼을 다 지난 뒤에만 보인다. */}
      <AdSlot slot="4300367266" />

      {saving && <Save debateId={debate.id} isPublic={debate.isPublic === true} onClose={() => setSaving(false)} />}
    </main>
  );
}

function Gap({ title, items }) {
  if (!items?.length) {
    return (
      <section className="card dashed" style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
        <h2 className="label">{title}</h2>
        <span className="note">이번엔 없어요</span>
      </section>
    );
  }
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <h2 className="label">{title}</h2>
      {items.map((x, i) => (
        <div key={i} style={{ padding: "14px 16px", border: "1px solid var(--line)", borderRadius: 4, background: "var(--red)", fontSize: 14, fontWeight: 800, lineHeight: 1.55, textWrap: "pretty" }}>
          {x}
        </div>
      ))}
    </section>
  );
}
